import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { WarningAlert } from 'components/blocks/warning-alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { useUpdatePromptFolder } from '../hooks/use-prompts';
import type { PromptFolder } from '../types';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';

const editFolderSchema = z.object({
  name: z.string().trim().min(1, 'Folder name is required'),
  visibility: z.enum(['private', 'public']),
});

type EditFolderForm = z.infer<typeof editFolderSchema>;

const getPromptFolderVisibility = (folder: PromptFolder): ResourceVisibility => {
  const value = String(folder.visibility);
  if (value === 'private' || value === 'public') {
    return value;
  }
  if (value === 'only_you') {
    return 'private';
  }
  return DEFAULT_RESOURCE_VISIBILITY;
};

type EditFolderModalProps = {
  folder: PromptFolder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditFolderModal({ folder, open, onOpenChange }: EditFolderModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const canEdit = useIsOwner(folder);
  const { mutateAsync: updateFolder, isPending } = useUpdatePromptFolder();

  const form = useForm<EditFolderForm>({
    resolver: zodResolver(editFolderSchema),
    defaultValues: {
      name: folder.name,
      visibility: getPromptFolderVisibility(folder),
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: folder.name,
        visibility: getPromptFolderVisibility(folder),
      });
    }
  }, [folder, form, open]);

  const onSubmit = async (values: EditFolderForm) => {
    if (!canEdit) return;

    try {
      await updateFolder({
        id: folder.id,
        name: values.name,
        visibility: values.visibility,
      });
      toast({
        variant: 'success',
        title: t('FOLDER_UPDATED') || 'Folder updated',
        description: t('YOUR_FOLDER_NAME_WAS_SAVED') || 'Your folder name was saved.',
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('FAILED_TO_UPDATE_FOLDER') || 'Failed to update folder',
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const dialogTitle = canEdit
    ? t('EDIT_FOLDER') || 'Edit folder'
    : t('VIEW_FOLDER') || 'View folder';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(event) => {
          if (!canEdit) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          const input = (event.currentTarget as HTMLElement | null)?.querySelector<HTMLInputElement>(
            'input[name="name"]'
          );
          input?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!canEdit) return;
              void form.handleSubmit(onSubmit)();
            }}
            className="space-y-5"
          >
            <fieldset
              disabled={!canEdit || isPending}
              className="m-0 min-w-0 space-y-5 border-0 p-0 disabled:opacity-70"
            >
              {!canEdit ? (
                <WarningAlert>
                  {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_FOLDER') ||
                    'You are not the owner of this folder.'}
                </WarningAlert>
              ) : null}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('FOLDER_NAME') || 'Folder name'}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('FOLDER_NAME') || 'Folder name'} autoFocus={canEdit} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <VisibilityField
                        value={field.value}
                        onValueChange={field.onChange}
                        idPrefix="edit-prompt-folder-visibility"
                        disabled={!canEdit || isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {canEdit ? t('CANCEL') || 'Cancel' : t('CLOSE') || 'Close'}
              </Button>
              {canEdit ? (
                <Button type="submit" loading={isPending} disabled={isPending}>
                  {t('SAVE_CHANGES') || 'Save changes'}
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
